import React, { useRef, useEffect } from "react";
import TouchInputManager from "../managers/touch-input-manager";
import { vibratePress } from "../utils/vibration";
import "./TouchControls.css";

interface TouchControlsProps {
  touchInput: TouchInputManager;
}

export default function TouchControls({ touchInput }: TouchControlsProps) {
  const leftZoneRef = useRef<HTMLDivElement>(null);
  const rightZoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const leftZone = leftZoneRef.current;
    const rightZone = rightZoneRef.current;

    if (!leftZone || !rightZone) return;

    const vibrate = () => vibratePress();

    // Common Thresholds
    const MOVE_THRESHOLD = 20;

    // =========================
    // LEFT ZONE STATE
    // =========================
    let leftAction: 'down' | null = null;
    let leftStartX = 0;
    let leftStartY = 0;
    let leftLastX = 0;
    let leftIsHorizontal = false;

    const handleLeftStart = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      leftStartX = touch.clientX;
      leftStartY = touch.clientY;
      leftLastX = touch.clientX;
      leftAction = null;
      leftIsHorizontal = false;
    };

    const handleLeftMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      const deltaY = touch.clientY - leftStartY;
      const deltaX = touch.clientX - leftStartX;

      // Drag Down -> Soft Drop
      // Block if horizontal lock is active
      if (!leftIsHorizontal && deltaY > 50 && (Math.abs(deltaY) > Math.abs(deltaX) || leftAction === 'down')) {
        if (leftAction !== 'down') {
          leftAction = 'down';
          touchInput.pressButton('down');
          vibrate();
        }
        return;
      }
      // Release Soft Drop if moved back up
      else if (leftAction === 'down') {
        leftAction = null;
        touchInput.releaseButton('down');
      }

      // 1-to-1 Movement
      const currentX = touch.clientX;
      const diff = currentX - leftLastX;
      if (Math.abs(diff) >= MOVE_THRESHOLD) {
        leftIsHorizontal = true; // Lock horizontal
        const direction = diff > 0 ? 'right' : 'left';
        touchInput.pressButton(direction);
        vibrate();
        setTimeout(() => touchInput.releaseButton(direction), 30);
        leftLastX = currentX;
      }
    };

    const handleLeftEnd = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      const deltaY = touch.clientY - leftStartY;
      const deltaX = touch.clientX - leftStartX;

      // Release Soft Drop
      if (leftAction === 'down') {
        touchInput.releaseButton('down');
        leftAction = null;
      }

      // Swipe Down (Release) -> Hard Drop
      // Must be predominantly vertical and not horizontal locked
      if (!leftIsHorizontal && deltaY > 80 && Math.abs(deltaY) > Math.abs(deltaX)) {
        touchInput.pressButton('drop');
        vibrate();
        setTimeout(() => touchInput.releaseButton('drop'), 50);
        return;
      }

      // Tap -> Rotate
      if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
        touchInput.pressButton('rotate');
        vibrate();
        setTimeout(() => touchInput.releaseButton('rotate'), 50);
      }
    };

    // =========================
    // RIGHT ZONE STATE (Mirror of Left)
    // =========================
    let rightAction: 'down' | null = null;
    let rightStartX = 0;
    let rightStartY = 0;
    let rightLastX = 0;
    let rightIsHorizontal = false;

    const handleRightStart = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      rightStartX = touch.clientX;
      rightStartY = touch.clientY;
      rightLastX = touch.clientX;
      rightAction = null;
      rightIsHorizontal = false;
    };

    const handleRightMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      const deltaY = touch.clientY - rightStartY;
      const deltaX = touch.clientX - rightStartX;

      if (!rightIsHorizontal && deltaY > 50 && (Math.abs(deltaY) > Math.abs(deltaX) || rightAction === 'down')) {
        if (rightAction !== 'down') {
          rightAction = 'down';
          touchInput.pressButton('down');
          vibrate();
        }
        return;
      } else if (rightAction === 'down') {
        rightAction = null;
        touchInput.releaseButton('down');
      }

      const currentX = touch.clientX;
      const diff = currentX - rightLastX;
      if (Math.abs(diff) >= MOVE_THRESHOLD) {
        rightIsHorizontal = true;
        const direction = diff > 0 ? 'right' : 'left';
        touchInput.pressButton(direction);
        vibrate();
        setTimeout(() => touchInput.releaseButton(direction), 30);
        rightLastX = currentX;
      }
    };

    const handleRightEnd = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      const deltaY = touch.clientY - rightStartY;
      const deltaX = touch.clientX - rightStartX;

      if (rightAction === 'down') {
        touchInput.releaseButton('down');
        rightAction = null;
      }

      if (!rightIsHorizontal && deltaY > 80 && Math.abs(deltaY) > Math.abs(deltaX)) {
        touchInput.pressButton('drop');
        vibrate();
        setTimeout(() => touchInput.releaseButton('drop'), 50);
        return;
      }

      if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
        touchInput.pressButton('rotate');
        vibrate();
        setTimeout(() => touchInput.releaseButton('rotate'), 50);
      }
    };

    // =========================
    // EVENT BINDING
    // =========================
    leftZone.addEventListener('touchstart', handleLeftStart, { passive: false });
    leftZone.addEventListener('touchmove', handleLeftMove, { passive: false });
    leftZone.addEventListener('touchend', handleLeftEnd, { passive: false });
    leftZone.addEventListener('touchcancel', handleLeftEnd, { passive: false });

    rightZone.addEventListener('touchstart', handleRightStart, { passive: false });
    rightZone.addEventListener('touchmove', handleRightMove, { passive: false });
    rightZone.addEventListener('touchend', handleRightEnd, { passive: false });
    rightZone.addEventListener('touchcancel', handleRightEnd, { passive: false });

    return () => {
      leftZone.removeEventListener('touchstart', handleLeftStart);
      leftZone.removeEventListener('touchmove', handleLeftMove);
      leftZone.removeEventListener('touchend', handleLeftEnd);
      leftZone.removeEventListener('touchcancel', handleLeftEnd);

      rightZone.removeEventListener('touchstart', handleRightStart);
      rightZone.removeEventListener('touchmove', handleRightMove);
      rightZone.removeEventListener('touchend', handleRightEnd);
      rightZone.removeEventListener('touchcancel', handleRightEnd);

      // Safety: Clear all inputs on unmount to prevent stuck keys
      const actions = ['left', 'right', 'down', 'rotate', 'rotateTwice', 'drop'] as const;
      actions.forEach(action => touchInput.releaseButton(action));
    };

  }, [touchInput]);

  return (
    <div className="touch-controls">
      <div className="touch-controls-container">
        <div className="touch-zone touch-zone-left" ref={leftZoneRef} />
        <div className="touch-zone touch-zone-right" ref={rightZoneRef} />
      </div>
    </div>
  );
}
